import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { auth } from "../firebase";
import { signOut } from "firebase/auth";
import {
    FaShoppingBasket,
    FaSignOutAlt,
    FaSignInAlt,
    FaWallet,
    FaUserCircle,
    FaHistory,
    FaShoppingBag,
    FaFileAlt,
    FaInbox
} from "react-icons/fa";
import { getDatabase, ref, onValue, set } from "firebase/database"; // Import get untuk mengambil data secara langsung
import { toast } from "react-toastify"; // Untuk menampilkan notifikasi

const Navbar = () => {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [cartItemCount, setCartItemCount] = useState(0);
    const [saldo, setSaldo] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("dana");
    const [depositAmount, setDepositAmount] = useState(""); // State untuk jumlah deposit

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setIsLoggedIn(true);
                fetchUserSaldo(user.uid); // Ambil saldo dari Firebase
                fetchCartItems(user.uid); // Ambil item keranjang dari Firebase
            } else {
                setIsLoggedIn(false);
                setSaldo(0); // Reset saldo
                setCartItemCount(0); // Reset keranjang
            }
        });

        return () => unsubscribe();
    }, []);

    // Fungsi mengambil saldo pengguna dari Firebase
    const fetchUserSaldo = (uid) => {
        const db = getDatabase();
        const userRef = ref(db, `users/${uid}`);

        onValue(userRef, (snapshot) => {
            const data = snapshot.val();
            if (data && data.saldo !== undefined) {
                const saldoValue = parseFloat(data.saldo); // Konversi saldo ke angka
                setSaldo(!isNaN(saldoValue) ? saldoValue : 0); // Update saldo
            } else {
                setSaldo(0); // Jika tidak ada data saldo
            }
        });
    };

    // Fungsi mengambil jumlah item di keranjang
    const fetchCartItems = (uid) => {
        const db = getDatabase();
        const cartRef = ref(db, `cart/${uid}`);

        onValue(cartRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const totalItems = Object.values(data).reduce(
                    (acc, item) => acc + item.count,
                    0
                );
                setCartItemCount(totalItems);
            } else {
                setCartItemCount(0);
            }
        });
    };

    // Fungsi logout
    const handleLogout = async () => {
        try {
            await signOut(auth);
            setIsLoggedIn(false);
            setSaldo(0);
            setCartItemCount(0);
        } catch (error) {
            console.error("Logout error:", error);
        }
    };

    // Fungsi untuk membuka modal deposit
    const openDepositModal = () => {
        setIsModalOpen(true);
    };

    // Fungsi untuk menutup modal deposit
    const closeDepositModal = () => {
        setIsModalOpen(false);
        setDepositAmount(""); // Reset jumlah deposit
    };

    // Fungsi untuk menangani pemilihan metode pembayaran
    const handlePaymentMethodChange = (method) => {
        setSelectedPaymentMethod(method);
    };

    // Fungsi untuk menangani perubahan jumlah deposit
    const handleDepositAmountChange = (e) => {
        setDepositAmount(e.target.value);
    };

    // Fungsi untuk menyimpan deposit ke database
    const saveDepositToDatabase = async (uid, amount, method) => {
        // Generate ID unik menggunakan Math.random() dan Date.now()
        const depositId =
            "deposit_" +
            Math.random().toString(36).substr(2, 9) +
            Date.now().toString(36); // ID unik

        const db = getDatabase();
        const depositRef = ref(db, `deposits/${uid}/${depositId}`); // Menyimpan data deposit berdasarkan UID

        // Buat data deposit
        const newDeposit = {
            amount: amount,
            method: method,
            status: "pending", // Status awal sebagai pending
            timestamp: Date.now() // Waktu deposit
        };

        try {
            // Simpan data deposit
            await set(depositRef, newDeposit);
            toast.success("Deposit berhasil disimpan, menunggu konfirmasi!");
        } catch (error) {
            console.error("Gagal menyimpan deposit:", error);
            toast.error("Gagal menyimpan deposit. Coba lagi!");
        }
    };

    // Fungsi untuk melakukan deposit dengan validasi
    const handleDeposit = async () => {
        const deposit = parseFloat(depositAmount);

        // Validasi jumlah deposit
        if (!depositAmount || isNaN(deposit) || deposit <= 0) {
            toast.error("Harap masukkan jumlah deposit yang valid.");
            return;
        }

        if (deposit < 1000) {
            toast.error("Jumlah deposit minimal Rp 1.000.");
            return;
        }

        if (deposit > 5000000) {
            toast.error("Jumlah deposit maksimal Rp 5.000.000.");
            return;
        }

        // Simpan deposit ke database dengan status "pending"
        const user = auth.currentUser;
        if (user) {
            await saveDepositToDatabase(
                user.uid,
                deposit,
                selectedPaymentMethod
            );
        } else {
            toast.error("Anda harus login untuk melakukan deposit.");
            return;
        }

        // Tutup modal setelah berhasil
        closeDepositModal();
    };

    return (
        <div className="navbar bg-base-100 shadow-lg">
            <div className="flex-1">
                <Link to="/" className="btn btn-ghost normal-case text-xl">
                    PPOB App
                </Link>
            </div>
            <div className="flex items-center">
                {/* Link ke Inbox */}
                <Link to="/inbox" className="btn mr-2">
                    <FaInbox size={20} />
                </Link>
                {/* Keranjang Belanja */}
                <Link to="/checkout">
                    <button className="btn">
                        <FaShoppingBasket size={25} />
                        {cartItemCount > 0 && (
                            <div className="badge badge-secondary">
                                {cartItemCount > 99 ? "+99" : cartItemCount}
                            </div>
                        )}
                    </button>
                </Link>
            </div>
            <div className="flex-none">
                <div className="dropdown dropdown-end">
                    <label tabIndex={0} className="btn btn-ghost">
                        <div className="w-10 rounded-full">
                            <img
                                src="https://cdn-icons-png.freepik.com/128/3917/3917618.png"
                                style={{ width: "25px", height: "25px" }}
                                alt="Profile Icon"
                            />
                        </div>
                    </label>
                    <ul
                        tabIndex={0}
                        className="menu menu-sm dropdown-content mt-3 z-[1] p-2 shadow bg-base-100 rounded-box w-52"
                    >
                        {/* Tampilkan Saldo dengan Fungsi Menekan Saldo */}
                        <li
                            className="flex items-center justify-between"
                            onClick={openDepositModal} // Menambahkan aksi untuk membuka modal deposit saat saldo ditekan
                        >
                            <span className="font-semibold">
                                <FaWallet />
                                <span className="font-bold text-green-500 cursor-pointer">
                                    Rp {saldo.toLocaleString("id-ID")}
                                </span>
                            </span>
                        </li>
                        <li>
                            <Link to="/profile">
                                <FaUserCircle /> Profile
                            </Link>
                        </li>
                        <li>
                            <Link to="/status">
                                <FaHistory /> Status
                            </Link>
                        </li>
                        <li>
                            <Link to="/panduan">
                                <FaFileAlt /> Panduan
                            </Link>
                        </li>
                        <li>
                            <Link to="/order">
                                <FaShoppingBag /> Order
                            </Link>
                        </li>
                        {isLoggedIn ? (
                            <li>
                                <button
                                    onClick={handleLogout}
                                    className="text-red-500"
                                >
                                    <FaSignOutAlt /> Log Out
                                </button>
                            </li>
                        ) : (
                            <li>
                                <Link to="/login">
                                    <FaSignInAlt /> Login
                                </Link>
                            </li>
                        )}
                    </ul>
                </div>
            </div>
            {/*Modal Deposit*/}
            {isModalOpen && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg w-96">
                        <h2 className="text-xl font-semibold mb-4">
                            Deposit Saldo
                        </h2>

                        {/* Input Jumlah Deposit */}
                        <div className="mb-4">
                            <p>
                                Lihat panduan untuk cara deposit saldo{" "}
                                <Link className="text-red-500" onClick={closeDepositModal} to="/panduan">
                                    panduan
                                </Link>
                            </p>
                            <label className="block font-semibold">
                                Jumlah Deposit (Rp):
                            </label>
                            <input
                                type="number"
                                value={depositAmount}
                                onChange={handleDepositAmountChange}
                                className="input input-bordered w-full"
                                placeholder="Masukkan jumlah deposit"
                            />
                        </div>

                        {/* Pilihan Metode Pembayaran */}
                        <div className="flex gap-4 mb-4">
                            <button
                                className={`btn ${
                                    selectedPaymentMethod === "dana"
                                        ? "btn-active"
                                        : ""
                                }`}
                                onClick={() =>
                                    handlePaymentMethodChange("dana")
                                }
                            >
                                DANA
                            </button>
                            <button
                                className={`btn ${
                                    selectedPaymentMethod === "seabank"
                                        ? "btn-active"
                                        : ""
                                }`}
                                onClick={() => handlePaymentMethodChange("seabank")}
                            >
                                SEA BANK
                            </button>
                            <button
                                className={`btn ${
                                    selectedPaymentMethod === "gopay"
                                        ? "btn-active"
                                        : ""
                                }`}
                                onClick={() =>
                                    handlePaymentMethodChange("gopay")
                                }
                            >
                                Gopay
                            </button>
                        </div>

                        {/* Tombol Deposit */}
                        <button
                            onClick={handleDeposit}
                            className="btn btn-success w-full"
                        >
                            Deposit
                        </button>

                        <button
                            onClick={closeDepositModal}
                            className="btn btn-ghost mt-4 w-full"
                        >
                            Tutup
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Navbar;
